import React, { Suspense, useEffect } from 'react';

import { CssBaseline } from '@material-ui/core';
import axios, { AxiosRequestConfig } from 'axios';
import cookie from 'cookie_js';
import * as log from 'loglevel';
import objectFitImages from 'object-fit-images';
import * as Raven from 'raven-js';
import { addTranslationForLanguage, initialize } from 'react-localize-redux';
import { connect } from 'react-redux';
import { add as rrpAddToPermissionsAction } from 'react-redux-permissions';
import { Redirect, Route, Router } from 'react-router';
import ResizeObserver from 'resize-observer-polyfill';

import * as BuildConstants from '../BuildConstants';
import { LoadingSpinner } from '../components/layouts';
import { getTranslationOrDefault } from '../localization';
import { StylesProvider, ThemeProvider } from '../style';
import { WebOrderTheme } from '../style/ThemeProvider';
import { NotificationsProvider } from '../utils/providers';
import { domUtils } from '../weborder/utils';
import * as Actions from './actions/Actions';
import * as analytics from './analytics';
import { Bounce, LoadingPanel, SetCookie } from './component';
import * as Intercom from './intercom';
import { addMetaData, simulateStartUrl } from './MetaData';
import registerServiceWorker from './registerServiceWorker';
import { flagService, history } from './services';
import { storage } from './services/storageServices';
import * as Settings from './SettingsStore';
import * as Store from './Store';
import { Utilities } from './Utilities';
import { addToHomescreenInit } from './vendor/AddToHomescreen';

const FastClick = require('fastclick');

const Checkout = React.lazy(() => import('./component/Checkout'));
const CollectionList = React.lazy(() => import('./component/CollectionList'));
const ConcessionList = React.lazy(() => import('./component/ConcessionList'));
const ConfigureItemHolder = React.lazy(() =>
  import('./component/ConfigureItemHolder'),
);
const CreditCard = React.lazy(() => import('./component/CreditCard'));
const CustomLocation = React.lazy(() => import('./component/CustomLocation'));
const Delivery = React.lazy(() => import('./component/Delivery'));
const DeliveryList = React.lazy(() => import('./component/DeliveryList'));
const Map = React.lazy(() => import('./component/Map'));
const MenuContainer = React.lazy(() => import('./component/MenuContainer'));
const NewLocation = React.lazy(() => import('./component/NewLocation'));
const OrderView = React.lazy(() => import('./component/OrderView'));
const Over18 = React.lazy(() => import('./component/Over18'));
const Payment = React.lazy(() => import('./component/Payment'));
const PhoneNumber = React.lazy(() => import('./component/PhoneNumber'));
const Restaurant = React.lazy(() => import('./component/Restaurant'));
const SmsCode = React.lazy(() => import('./component/SmsCode'));
const Unavailable = React.lazy(() => import('./component/Unavailable'));
const Voucher = React.lazy(() => import('./component/Voucher'));
const Where = React.lazy(() => import('./component/Where'));

const isProduction = process.env.NODE_ENV === 'production';
const environment = /master/.test(BuildConstants.GIT_BRANCH)
  ? 'production'
  : 'development';
const sentryKey =
  environment === 'production'
    ? 'https://c77a4c3f896c49e1a48855e5a4979fb3@sentry.io/105860'
    : 'https://cd95d48687a54ee1840a16ecef394c93@sentry.io/75499';

const applicationServerPublicKey =
  'BHPXmGnT3r23qsRjaCWNWwXJ4bg7q7aASszB4sGanqscSvr7-MmIZBT6T822KZCQ1t8YadHFYvQNYe7SnpC7yDo';

const addToPermissionsAction = (types: Required<Fd.App['AppResourceSet']>) =>
  rrpAddToPermissionsAction(types);

//#region AXIOS RETRY MIDDLEWARE
declare module 'axios' {
  interface AxiosRequestConfig {
    retry?: number;
    retryDelay?: number;
  }
}
axios.interceptors.response.use(undefined, function axiosRetryInterceptor(err) {
  const config = err.config;
  // If config does not exist or the retry option is not set, reject
  if (!config || !config.retry) return Promise.reject(err);

  // Set the variable for keeping track of the retry count
  config.__retryCount = config.__retryCount || 0;

  // Check if we've maxed out the total number of retries
  if (config.__retryCount >= config.retry) {
    // Reject with the error
    return Promise.reject(err);
  }

  // Increase the retry count
  config.__retryCount += 1;

  // Create new promise to handle exponential backoff
  const backoff = new Promise<void>(function(resolve) {
    setTimeout(function() {
      resolve();
    }, config.retryDelay || 1);
  });

  // Return the promise in which recalls axios to retry the request
  return backoff.then(function() {
    return axios(config);
  });
});
//#endregion

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

type InnerProps = StateProps & DispatchProps;
type OuterProps = {
  initialState?: any;
  children?: React.ReactNode;
  settings: any;
  store?: any;
};
type Props = InnerProps & OuterProps;

function Weborder(props: Props) {
  const {
    appConfig,
    deviceId,
    flagServiceStatus,
    flagUiRedesignEnabled,
    flagUiRedesignReady,
    setAppDesignScheme,
    setIsDarkTheme,
    setTheme,
    settings,
    themeName,
  } = props;

  let store: any = props.store || false;
  // const settings = props.settings;

  useEffect(() => {
    // Print to console build details
    console.log(
      `Branch:${BuildConstants.GIT_BRANCH} Hash:${BuildConstants.GIT_HASH} Build No.:${BuildConstants.BUILD_NUM}`,
    );
  }, []);

  useEffect(() => {
    if (
      process.env.REACT_APP_ENV === 'test' ||
      process.env.NODE_ENV === 'test'
    ) {
      settings.restaurant = 'system-test-wl';
    }
  }, []);

  useEffect(() => {
    if (!store) {
      const loadedGeo = Settings.LocationStore.Load();
      setupRaven();
      setUserSubscription();

      setupAxios(loadedGeo);
      initDOMandPolyfills();
      store = initialiseStore(loadedGeo);
    }
  }, []);

  useEffect(() => {
    if (settings.initialRoute) {
      history.replace(settings.initialRoute);
    }
  }, []);

  useEffect(() => {
    if (flagServiceStatus === 'NOT_READY' && appConfig.status === 'success') {
      const store = Store.getStoreInstance();
      const featureFlagParams = {
        deviceId,
        whitelabel_id: appConfig.data.WhiteLabelId,
        whitelabelId: appConfig.data.WhiteLabelId,
      };
      flagService.initialiseSplit(store, featureFlagParams);
    }
  }, [appConfig.status, flagServiceStatus]);

  useEffect(() => {
    if (appConfig.status === 'success' && flagServiceStatus !== 'NOT_READY') {
      const appDesignScheme = appConfig.data.AppDesignScheme;
      let font = '';

      if (
        flagServiceStatus === 'READY' &&
        flagUiRedesignEnabled &&
        flagUiRedesignReady
      ) {
        const primary = Utilities.returnColor(
          appDesignScheme.Primary,
          BuildConstants.PRIMARY_COLOR,
        );
        const secondary = Utilities.returnColor(
          appDesignScheme.Secondary,
          BuildConstants.THEME_COLOR,
        );
        const tertiary = Utilities.returnColor(
          appDesignScheme.Tertiary,
          BuildConstants.TERTIARY_COLOR,
        );

        font = appDesignScheme.Font || 'Poppins';
        domUtils.loadFont(font);

        setAppDesignScheme({
          Font: font,
          Primary: primary,
          Secondary: secondary,
          Tertiary: tertiary,
        });
        setTheme('budapest');
      } else {
        const primary = Utilities.returnColor(
          appDesignScheme.Tertiary,
          BuildConstants.PRIMARY_COLOR,
        );
        const secondary = Utilities.returnColor(
          appDesignScheme.Primary,
          BuildConstants.THEME_COLOR,
        );
        const tertiary = Utilities.returnColor(
          appDesignScheme.Secondary,
          BuildConstants.TERTIARY_COLOR,
        );

        font = appDesignScheme.Font || 'Roboto';
        domUtils.loadFont(font, false);
        domUtils.applyLegacyCss(font, primary, secondary, tertiary);

        setAppDesignScheme({
          Font: font,
          Primary: primary,
          Secondary: secondary,
          Tertiary: tertiary,
        });
        setIsDarkTheme(settings.theme === 'dark');
        setTheme('alpha');
      }
    }
  }, [flagServiceStatus, flagUiRedesignEnabled, flagUiRedesignReady]);

  const setupRaven = () => {
    if (isProduction) {
      Raven.config(sentryKey, {
        release: BuildConstants.BUILD_NUM,
        environment,
        instrument: {
          tryCatch: false,
        },
        ignoreErrors: [
          'undefined is not an object (evaluating \'jQuery("#map").offset().top\')',
          'Request failed with status code 403',
          'Network Error',
          'Error loading past delivery locations',
          'Error loading collection restaurant',
          'Error loading payment accounts',
          'Order submission failure',
          'Could not load "geocoder"',
          'Could not load "places_impl"',
          'Geolocation error callback',
          "Cannot read property 'top' of undefined",
          'Request failed with status code 500',
          'Error adding card',
          'https://api.flipdish.co/api/events/',
        ],
      }).install();
    }
  };

  const initialiseStore = (loadedGeo) => {
    const {
      addTranslationForLanguage,
      loadAppConfig,
      loadCustomer,
      setLocation,
      loadPhoneNumberConfig,
      setLocaleCode,
      setOffset,
      setPermissions,
    } = props;

    if (settings.offset !== null && !isNaN(parseInt(settings.offset, 10))) {
      setOffset(parseInt(settings.offset, 10));
    }

    const woStore = Store.getStoreInstance();
    (window as any)._flipdish = {
      setLanguage: (lang: string) => {
        setLanguage(woStore.getState(), woStore.dispatch, lang);
      },
      getLanguage: () => {
        const currentLanguageInStorage = storage.getItem(
          'flipdish-current-language',
        );
        return currentLanguageInStorage;
      },
    };

    const language =
      Utilities.extraOption('data-language') ||
      // used by external embeds
      Utilities.extraOption('data-initial-language') ||
      'en';

    const [localeCode, langJsonWithFallback] = getTranslationOrDefault(
      language,
    );

    setLocaleCode(localeCode);
    addTranslationForLanguage(
      Utilities.translateTransform(langJsonWithFallback),
      localeCode,
    );

    setPermissions();
    loadAppConfig();
    loadCustomer();

    if (loadedGeo) {
      setLocation(loadedGeo);
    }

    const store = Store.getStoreInstance();
    Store.watchForLocationChanges(store, (geo) => {
      loadPhoneNumberConfig(geo);
    });
  };

  const setLanguage = (store: any, dispatch: any, lang: string) => {
    if (
      (window as any)._flipdish &&
      (window as any)._flipdish.getLanguage &&
      (window as any)._flipdish.getLanguage() != lang
    ) {
      const { locale } = store;
      if (locale.languages.some((l) => l.code === lang)) {
        dispatch(Actions.ChangeLanguage(lang));
      }
    }
  };

  const setUserSubscription = () => {
    registerServiceWorker();
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      (navigator as any).serviceWorker.ready.then((swReg: any) => {
        return swReg.pushManager.getSubscription().then((subscription: any) => {
          const isSubscribed = !(subscription === null);

          if (isSubscribed) {
            log.info('User IS subscribed.');
          } else {
            log.info('User is NOT subscribed.');
            const applicationServerKey = Utilities.urlB64ToUint8Array(
              applicationServerPublicKey,
            );
            swReg.pushManager
              .subscribe({
                userVisibleOnly: true,
                applicationServerKey,
              })
              .then(() => {
                log.info('User is subscribed.');
                // TODO: Update subscription on server
              })
              .catch((err: any) => {
                log.info('Failed to subscribe the user: ', err);
              });
          }
        });
      });
    }
  };

  const setupAxios = (loadedGeo) => {
    const axiosConfig = (
      config: AxiosRequestConfig,
      baseURL: string,
      whiteLabel: string,
    ) => {
      config.headers.common['Flipdish-White-Label-Id'] = whiteLabel;
      config.headers.common['Content-Type'] = 'application/json; charset=utf-8';
      if (loadedGeo) {
        config.headers.common[
          'X-Coordinates'
        ] = `${loadedGeo.Latitude},${loadedGeo.Longitude},${Store.LocationCertainty.Settings}`;
      }
      config.baseURL = baseURL;
      config.withCredentials = true;

      if (Utilities.isAndroid && settings.pwa && Utilities.isStandalone) {
        config.headers.common['Flipdish-App-Type'] = 'PwaAndroid';
      } else if (
        Utilities.isIOSPhone &&
        settings.pwa === 'true' &&
        Utilities.isStandalone
      ) {
        config.headers.common['Flipdish-App-Type'] = 'PwaIos';
      } else {
        config.headers.common['Flipdish-App-Type'] = 'Web';
      }

      const flipdishToken = cookie.get('flipdish-token');
      if (flipdishToken) {
        axios.defaults.headers.common['X-Flipdish-Token'] = flipdishToken;
      }
    };

    if (settings?.restaurant) {
      // TODO - handle the case where whiteLabel is null
      // alert the developer.
      axiosConfig(axios.defaults, settings.server, settings.restaurant);
    }
  };

  const initDOMandPolyfills = () => {
    const readonly = Utilities.extraOption('data-menuid');

    if (!readonly && process.env.NODE_ENV !== 'test') {
      try {
        // Create a single ResizeObserver instance to handle all
        // container elements. The instance is created with a callback,
        // which is invoked as soon as an element is observed as well
        // as any time that element's size changes.
        const ro = new ResizeObserver((entries) => {
          const defaultBreakpoints = { XS: 384, SM: 768, MD: 992, LG: 1200 };

          entries.forEach((entry) => {
            // If breakpoints are defined on the observed element,
            // use them. Otherwise use the defaults.
            const breakpoints = (entry.target as any).dataset.breakpoints
              ? JSON.parse((entry.target as any).dataset.breakpoints)
              : defaultBreakpoints;

            // Update the matching breakpoints on the observed element.
            Object.keys(breakpoints).forEach((breakpoint) => {
              const minWidth = breakpoints[breakpoint];
              if (entry.contentRect.width >= minWidth) {
                entry.target.classList.add(breakpoint);
              } else {
                entry.target.classList.remove(breakpoint);
              }
            });
          });
        });

        ro.observe(Utilities.flipdiv);
      } catch (error) {}
    }

    // Run the polyfills for IE
    objectFitImages();

    //For safari 12.2 iframe for the cookies
    const isSafari =
      navigator.vendor &&
      navigator.vendor.indexOf('Apple') > -1 &&
      navigator.userAgent;

    if (isSafari) {
      const iframe = document.createElement('iframe');
      iframe.style.height = '1px';
      iframe.style.width = '1px';
      iframe.style.border = '0';
      iframe.style.position = 'absolute';
      iframe.src =
        'https://web-order.flipdish.co/production/embedded/latest/index.html';
      document.body.appendChild(iframe);
    }

    // http://stackoverflow.com/questions/3885018/active-pseudo-class-doesnt-work-in-mobile-safari
    // Add an event listener to force mobile safari to  set the :active class on elements
    // so that we can see buttons being depressed when we click on them.
    document.addEventListener('touchstart', () => {}, false);

    // For iOS bg color on bounce
    if (settings.theme === 'dark') {
      document.body.style.backgroundColor = '#414142';
    }

    //If readonly PWA stuff not required
    if (readonly) {
      return;
    }

    // Initialize the Add to Homescreen call out
    addToHomescreenInit();

    // Add manifest
    const manifestExists = document.querySelector("link[rel='manifest']");

    if (!manifestExists) {
      const manifestTag = document.createElement('link');
      manifestTag.href = process.env.PUBLIC_URL + '/manifest.json';
      manifestTag.rel = 'manifest';
      document.head.appendChild(manifestTag);
    }

    // Add theme color meta tag
    const themeMetaExists = document.querySelector("meta[name='theme-color']");

    if (!themeMetaExists) {
      const themeMeta = document.createElement('meta');
      themeMeta.name = 'theme-color';
      themeMeta.content = BuildConstants.THEME_COLOR;
      document.head.appendChild(themeMeta);
    }

    //Add viewport meta tag
    const viewportMetaExists = document.querySelector("meta[name='viewport']");

    if (!viewportMetaExists) {
      const viewportMeta = document.createElement('meta');
      viewportMeta.name = 'viewport';
      viewportMeta.content =
        'width=device-width,initial-scale=1,shrink-to-fit=no';
      document.head.appendChild(viewportMeta);
    }

    if (Utilities.isIOS || Utilities.isAndroid) {
      addMetaData();

      // Delete all elements
      if (settings.fullScreenOnMobile !== 'false' && Utilities.flipdiv) {
        document.body.innerHTML = '';
        document.body.style.backgroundImage = 'none';
        if (Utilities.flipdiv) {
          document.body.appendChild(Utilities.flipdiv);
        }
      }

      if (Utilities.isAndroid) {
        const meta = document.createElement('meta');
        meta.name = 'mobile-web-app-capable';
        meta.content = 'yes';
        document.head.appendChild(meta);
      } else if (Utilities.isIOS) {
        if (settings.pwa === 'true') {
          FastClick.attach(document.body, {});
        }
      }

      if (!(settings.pwa === 'true')) {
        simulateStartUrl();
      } else if (settings.pwa === 'true' && Utilities.isStandalone) {
        require('./pwa.scss');
      }
    }
  };

  const logPageView = () => {
    analytics.PageView();
    if (store) {
      Intercom.Update(store.getState().intercom);
    }
    Utilities.setInLocalStorage('flipdish-last-url', window.location.hash);
  };

  const loader =
    themeName === 'budapest' ? <LoadingSpinner /> : <LoadingPanel />;

  if (appConfig.status !== 'success' || flagServiceStatus === 'NOT_READY') {
    return loader;
  }

  const storeLastPhysicalRestaurantId = (restaurantId: string): void => {
    Utilities.storeLastPhysicalRestaurantId(
      appConfig.data.AppNameId,
      restaurantId,
    );
  };

  const base = settings.baseHref;
  const webOrderRoutes = (
    <Suspense fallback={loader}>
      <Router history={history}>
        <Route path={base + 'bounced/:bounceurl'} component={Bounce} />
        <Route path={base} component={SetCookie}>
          <Route
            path="payment/:vrid/:type/:id"
            component={Payment}
            onEnter={() => logPageView()}
          />
          <Route
            path="card/:backurl"
            component={CreditCard}
            onEnter={() => logPageView()}
          />
          <Route
            path="phone(/:dlid)"
            component={PhoneNumber}
            onEnter={() => logPageView()}
          />
          <Route
            path="code/:phone(/:dlid)"
            component={SmsCode}
            onEnter={() => logPageView()}
          />
          <Route
            path="order/:orderId(/:concessionStoreId)"
            component={OrderView}
            onEnter={() => logPageView()}
          />
          <Route
            path="restaurant/:vrid"
            component={MenuContainer}
            onEnter={() => logPageView()}
          />
          <Route path="where" component={Where} onEnter={() => logPageView()} />
          <Route
            path="unavailable"
            component={Unavailable}
            onEnter={() => logPageView()}
          />
          <Route
            path="concession/:vrid/:prid"
            component={ConcessionList}
            onEnter={() => logPageView()}
          />
          <Route
            path="collection"
            component={CollectionList}
            onEnter={() => logPageView()}
          />
          <Route
            path="delivery"
            component={Delivery}
            onEnter={() => logPageView()}
          />
          <Route
            path="delivery/new"
            component={NewLocation}
            onEnter={() => logPageView()}
          />
          <Route
            path="delivery/map"
            component={Map}
            onEnter={() => logPageView()}
          />
          <Route
            path="delivery/custom/:lat/:lng"
            component={CustomLocation}
            onEnter={() => logPageView()}
          />
          <Route
            path="delivery/confirm(/:lat/:lng)"
            component={CustomLocation}
            onEnter={() => logPageView()}
          />
          <Route
            path="delivery/:location"
            component={DeliveryList}
            onEnter={() => logPageView()}
          />
          <Route
            path="restaurant/:vrid/:type/:id(/:concessionStoreId)"
            component={Restaurant}
            onEnter={(route) => {
              logPageView();
              storeLastPhysicalRestaurantId(route.params.id);
            }}
          />
          <Route
            path="over18/:vrid/:type/:id(/:concessionStoreId)"
            component={Over18}
            onEnter={() => logPageView()}
          />
          <Route
            path="checkout/:vrid/:type/:id(/:concessionStoreId)"
            component={Checkout}
            onEnter={() => logPageView()}
          />
          <Route
            path="restaurant/:vrid/:type/:id(/:concessionStoreId)/configure/:item/:index"
            component={ConfigureItemHolder}
            onEnter={() => logPageView()}
          />
          <Route
            path="voucher/:vrid/:type/:id/:paymentAccount(/:concessionStoreId)"
            component={Voucher}
            onEnter={() => logPageView()}
          />
        </Route>
        <Redirect from={base + '*'} to={base + 'where'} />
      </Router>
    </Suspense>
  );

  if (themeName === 'budapest') {
    return (
      <StylesProvider namespace="fd-budapest">
        <ThemeProvider themeName={themeName}>
          <CssBaseline />

          <NotificationsProvider>
            {props.children || webOrderRoutes}
          </NotificationsProvider>
        </ThemeProvider>
      </StylesProvider>
    );
  }

  return (
    <div id="flipdish-alpha">
      <StylesProvider namespace="fd-alpha">
        <ThemeProvider themeName={themeName}>
          {props.children || webOrderRoutes}
        </ThemeProvider>
      </StylesProvider>
    </div>
  );
}

const mapStateToProps = (state: Store.State) => {
  const uiRedesignFlagValue = flagService.getFlagValue(state, 'uiRedesign');

  return {
    appConfig: state.flipdish.AppConfig,
    deviceId: state.flipdish.Config.DeviceId,
    flagUiRedesignEnabled: flagService.isFlagOn(state, 'uiRedesign'),
    flagUiRedesignReady: uiRedesignFlagValue !== 'control',
    flagServiceStatus: flagService.getFlagServiceStatus(state),
    themeName: state.flipdish.themeName,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  const StoreLocationCertainlySettings = Store.LocationCertainty.Settings;

  return {
    addTranslationForLanguage: (translationSet, localeCode) => {
      dispatch(addTranslationForLanguage(translationSet, localeCode));
    },

    loadAppConfig: () => {
      dispatch(Actions.LoadAppconfig() as any);
    },
    loadCustomer: () => {
      dispatch(Actions.LoadCustomer() as any);
    },
    loadPhoneNumberConfig: (geo: any) => {
      dispatch(Actions.LoadPhoneNumberConfig(geo) as any);
    },

    setAppDesignScheme: (appDesignScheme: any) => {
      dispatch(Actions.SetAppDesignScheme(appDesignScheme));
    },
    setIsDarkTheme: (isDark: boolean) => {
      dispatch(Actions.SetIsDarkTheme(isDark));
    },
    setLocaleCode: (localeCode: any) => {
      dispatch(initialize([localeCode]));
    },
    setLocation: (loadedGeo: any) => {
      dispatch(Actions.AddLocation(loadedGeo, StoreLocationCertainlySettings));
    },
    setOffset: (offset: number) => {
      dispatch(Actions.SetOffset(offset));
    },
    setPermissions: () => {
      dispatch(
        addToPermissionsAction([
          'UpdateMenu',
          'UpdateMenuItemsHideTemporarily',
        ]),
      );
    },
    setTheme: (theme: WebOrderTheme) => {
      dispatch(Actions.SetTheme(theme));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Weborder);
